import { createContext, useContext, useState, useEffect, useMemo } from "react";
// Firebase imports moved to dynamic loading to prevent blocking

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // ✅ Firebase authenticated user
  const [guest, setGuest] = useState(null); // ✅ Guest user from MySQL
  const [loading, setLoading] = useState(false); // ✅ Start as false - no blocking
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);

  useEffect(() => {
    let unsubscribe = null;

    // Simplified initialization - let Firebase handle everything
    const initializeFirebaseAuth = async () => {
      try {
        console.log("🔄 Starting Firebase initialization...");
        
        // Dynamic import Firebase to prevent blocking
        const { auth } = await import("../config/firebase");
        const { onAuthStateChanged, signOut } = await import("firebase/auth");
        const { getGuestId, checkGuestInMySQL } = await import("../utils/guest");
        
        console.log("✅ Firebase modules imported successfully");
        setLoading(true);
        setFirebaseInitialized(true); // ✅ Mark Firebase as initialized
        
        unsubscribe = onAuthStateChanged(auth, async (authUser) => {
          console.log("🔔 onAuthStateChanged fired with user:", authUser ? authUser.uid : "null");
          
          if (authUser) {
            // ✅ User is logged in via Firebase
            console.log("🔥 Setting authenticated user:", authUser.uid);
            setUser(authUser);
            setGuest(null);
            localStorage.setItem('user', JSON.stringify(authUser));
          } else {
            // ✅ No Firebase user? Check if the guest exists in MySQL
            console.log("👤 No Firebase user, checking for guest...");
            try {
              const guestId = await getGuestId();
              const guestData = await checkGuestInMySQL(guestId);

              if (guestData) {
                console.log("👤 Setting guest user:", guestData);
                setGuest(guestData);
                localStorage.setItem('guest', JSON.stringify(guestData));
              } else {
                console.log("❌ No guest data found");
                setUser(null);
                setGuest(null);
              }
            } catch (error) {
              console.error("Error checking guest user:", error);
              setUser(null);
              setGuest(null);
            }
          }
          setLoading(false);
          console.log("✅ Auth state update complete");
        });
        
        console.log("🔥 Firebase Auth listener set up successfully");
      } catch (error) {
        console.error("❌ Firebase auth initialization error:", error);
        setLoading(false);
      }
    };

    // Execute Firebase initialization immediately
    initializeFirebaseAuth();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // ✅ Logout function (Clears both user and guest session)
  const logout = async () => {
    if (user) {
      try {
        // Dynamic import Firebase auth for logout
        const { auth } = await import("../config/firebase");
        const { signOut } = await import("firebase/auth");
        
        await signOut(auth);
        setUser(null);
        localStorage.removeItem('user');
        window.location.href = "/"; // ✅ Native redirect to homepage
      } catch (error) {
        console.error("Logout Error:", error);
        // Fallback: clear local state even if Firebase fails
        setUser(null);
        localStorage.removeItem('user');
        window.location.href = "/";
      }
    } else {
      // ✅ If guest, remove their stored ID and reset state
      localStorage.removeItem("guestId");
      localStorage.removeItem("guest");
      setGuest(null);
      window.location.href = "/"; // ✅ Native redirect to homepage
    }
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    console.log('🔍 AuthContext - Current state:', { 
      user: user ? user.uid : 'null', 
      guest: guest ? 'present' : 'null', 
      firebaseInitialized, 
      loading
    });
    
    return {
      user,
      guest,
      logout,
      firebaseInitialized,
      loading
    };
  }, [user, guest, firebaseInitialized, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children} {/* ✅ Always render children - no blocking */}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
